import {
  Component,
  Host,
  h,
  Prop,
  State,
  Element,
  getAssetPath,
  Method
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";

import { Locale } from "../../common/locale";
import {
  McpServerStatus,
  McpStartResult,
  McpStopResult
} from "./types";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/icon",
  "utils/layout",
  "utils/typography",
  "utils/elevation",
  "utils/spacing",
  "chameleon/scrollbar"
];

const COPY_ICON = getIconPath({
  category: "system",
  name: "copy",
  colorType: "primary"
});


@Component({
  tag: "gx-ide-mcp-server",
  styleUrl: "mcp-server.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/mcp-server"]
})
export class GxIdeMcpServer {
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  #chShortcutsEl: HTMLChShortcutsElement;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/mcp-server/shortcuts.json`
  );

  @Element() el: HTMLGxIdeMcpServerElement;

  @State() actionInProgress: boolean = false;
  @State() errorMessage: string | undefined;
  @State() copying: boolean = false;

  /**
   * When `true` the component shows a loader instead of content.
   * The host sets this to `false` once the initial status is known.
   */
  @Prop({ mutable: true }) initialLoading: boolean = true;

  /**
   * Current MCP server status. The host is responsible for keeping
   * this prop up to date (e.g. via polling or event-driven updates).
   * The component only mutates it internally during start/stop
   * transitions (`starting`, `stopping`, result states).
   */
  @Prop({ mutable: true }) status!: McpServerStatus;

  /**
   * Port the MCP server is listening on (shown when running).
   */
  @Prop({ mutable: true }) serverPort: number | undefined;

  /**
   * Full endpoint URL (shown when running).
   */
  @Prop({ mutable: true }) serverEndpoint: string | undefined;

  /**
   * Callback to start the MCP server. Returns result with connection info.
   */
  @Prop() readonly startCallback!: StartCallback;

  /**
   * Callback to stop the MCP server.
   */
  @Prop() readonly stopCallback!: StopCallback;

  /**
   * Callback to copy text to the clipboard. Delegated to the IDE clipboard
   * service so the host environment controls the copy behavior.
   */
  @Prop() readonly copyCallback!: CopyCallback;

  /**
   * Callback to close/dismiss the dialog.
   */
  @Prop() readonly closeCallback!: CloseCallback;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
  }

  /**
   * Suspends or reactivates the shortcuts.
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    this.#chShortcutsEl.suspend = suspendShortcuts;
  }

  #startHandler = async () => {
    this.errorMessage = undefined;
    this.actionInProgress = true;

    try {
      const result = await this.startCallback();

      if (!result.success) {
        this.errorMessage =
          result.errorMessage ??
          this.#componentLocale.errors.startFailed;
      }
    } catch {
      this.errorMessage = this.#componentLocale.errors.startFailed;
    } finally {
      this.actionInProgress = false;
    }
  };

  #stopHandler = async () => {
    this.errorMessage = undefined;
    this.actionInProgress = true;

    try {
      const result = await this.stopCallback();

      if (!result.success) {
        this.errorMessage =
          result.errorMessage ??
          this.#componentLocale.errors.stopFailed;
      }
    } catch {
      this.errorMessage = this.#componentLocale.errors.stopFailed;
    } finally {
      this.actionInProgress = false;
    }
  };

  #closeHandler = () => {
    this.closeCallback?.();
  };

  #copyEndpointHandler = () => {
    if (this.serverEndpoint && this.copyCallback) {
      this.copying = true;
      this.copyCallback(this.serverEndpoint);
      setTimeout(() => {
        this.copying = false;
      }, 1000);
    }
  };

  #renderStatusBadge() {
    const statusText =
      this.#componentLocale.status[this.status] ?? this.status;

    const badgeClass = `status-badge status-badge--${this.status}`;
    const isAnimated =
      this.status === "starting" || this.status === "stopping";

    return (
      <span class={badgeClass}>
        <span
          class={`status-dot ${isAnimated ? "status-dot--animated" : ""}`}
        ></span>
        <span class="body-regular-s">{statusText}</span>
      </span>
    );
  }

  #renderStatusCard() {
    if (this.status !== "running" && this.status !== "stopping") {
      return null;
    }

    const isDimmed = this.status === "stopping";

    return (
      <div
        class={`status-card elevation-1 ${isDimmed ? "status-card--dimmed" : ""}`}
      >
        <div class="status-card__row">
          <span class="body-semi-bold-s">
            {this.#componentLocale.statusCard.statusLabel}
          </span>
          <span class="body-regular-s">
            {this.#componentLocale.status[this.status]}
          </span>
        </div>

        {this.serverPort !== undefined && (
          <div class="status-card__row">
            <span class="body-semi-bold-s">
              {this.#componentLocale.statusCard.portLabel}
            </span>
            <span class="body-regular-s">{this.serverPort}</span>
          </div>
        )}

        {this.serverEndpoint && (
          <div class="status-card__row">
            <span class="body-semi-bold-s">
              {this.#componentLocale.statusCard.endpointLabel}
            </span>
            <span class="status-card__endpoint">
              <span class="body-regular-s endpoint-value">
                {this.serverEndpoint}
              </span>
              <button
                class="button-tertiary button-icon-only"
                type="button"
                onClick={this.#copyEndpointHandler}
                disabled={this.copying}
                aria-label={this.#componentLocale.statusCard.copyEndpoint}
                title={
                  this.copying
                    ? this.#componentLocale.statusCard.copiedEndpoint
                    : this.#componentLocale.statusCard.copyEndpoint
                }
              >
                <ch-image class="icon-m" src={COPY_ICON}></ch-image>
              </button>
            </span>
          </div>
        )}
      </div>
    );
  }

  #renderError() {
    if (!this.errorMessage) {
      return null;
    }

    return <div class="error-message body-regular-s">{this.errorMessage}</div>;
  }

  #renderPrimaryButton() {
    const isRunning =
      this.status === "running" || this.status === "stopping";
    const isTransitioning =
      this.status === "starting" || this.status === "stopping";
    const isDisabled = this.actionInProgress || isTransitioning;

    if (isRunning) {
      return (
        <button
          class="button-primary primary-action"
          type="button"
          onClick={this.#stopHandler}
          disabled={isDisabled}
          part="button-confirm"
        >
          {this.#componentLocale.buttons.stop}
        </button>
      );
    }

    return (
      <button
        class="button-primary primary-action"
        type="button"
        onClick={this.#startHandler}
        disabled={isDisabled}
        part="button-confirm"
      >
        {this.#componentLocale.buttons.start}
      </button>
    );
  }

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section class="section">
          <header class="header dialog-header-with-border spacing-body-inline spacing-body-block-start">
            <h1 class="heading-s title">
              {this.#componentLocale.header.title}
            </h1>
            {!this.initialLoading && this.#renderStatusBadge()}
          </header>

          <div class="main spacing-body-inline">
            {this.initialLoading ? (
              <gx-ide-loader
                loaderTitle={this.#componentLocale.loader.title}
                show
                class="loader"
              ></gx-ide-loader>
            ) : ([
              <div class="info-section">
                <p class="body-regular-m">
                  {this.#componentLocale.info.description}
                </p>
                <p class="body-regular-m">
                  {this.#componentLocale.info.details}
                </p>
              </div>,

              this.#renderStatusCard(),
              this.#renderError()
            ])}
          </div>

          <footer class="footer dialog-footer-with-border spacing-body-inline spacing-body-block-end">
            <div class="buttons-spacer">
              <button
                class="button-secondary"
                type="button"
                onClick={this.#closeHandler}
                part="button-cancel"
              >
                {this.#componentLocale.buttons.close}
              </button>
              {!this.initialLoading && this.#renderPrimaryButton()}
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export type StartCallback = () => Promise<McpStartResult>;
export type StopCallback = () => Promise<McpStopResult>;
export type CopyCallback = (text: string) => void;
export type CloseCallback = () => void;
